import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Document, Page, Text, View, StyleSheet, Image, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../../../../config/constants";
import { getProtocolTitle } from "../../../../../../config/validateProtocols";
// Components
import Axios from "../../../../../../config/axios";
import { DownloadButton, FileImg } from "./styles";
// Assets
import PDFImg from "../../../../../../assets/icons/ico_pdf.svg";
import PDFDisabledImg from "../../../../../../assets/icons/ico_pdf_grey.svg";

const DownloadPDF = ({ sessionsData, plotsRef = {} }) => {
  const [patientUser, setPatientUser] = useState("");
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const { patientId, protocolId } = useParams();
  const [t] = useTranslation("global");

  // Get patient data
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`)
      .then(({ data }) => {
        setPatientUser(data.PATIENT_USER);
      })
      .catch((error) => {
        console.log("Get patient data: ", error);
      });

    return () => {
      setPatientUser("");
    };
  }, [patientId]);

  const styles = StyleSheet.create({
    page: {
      padding: 20,
      backgroundColor: "#e3e3e3",
    },
    title: {
      fontSize: 12,
      marginBottom: 5,
    },
    chartContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: 0,
    },
    chartImage: {
      marginTop: 10,
      marginBottom: 10,
      width: "100%",
      objectFit: "contain",
    },
  });

  const SessionsPDF = ({ plotsImg }) => {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>{`${t("downloadFiles_pdf_hospital")}: ${hospitalName}`}</Text>
          <Text style={styles.title}>{`${t("patient")}: ${patientUser}`}</Text>
          <Text style={styles.title}>{`${t("activity")}: ${t(getProtocolTitle("protocols", protocolId))}`}</Text>
          <View style={styles.chartContainer}>
            {plotsImg.map((img, index) => (
              <Image key={index} src={img} style={styles.chartImage} />
            ))}
          </View>
        </Page>
      </Document>
    );
  };

  const captureScorePlot = async () => {
    const plotsRefValues = Object.values(plotsRef);
    const filteredPlots = plotsRefValues.filter((plot) => plot.current);

    let plotsImg = [];
    for (const plot of filteredPlots) {
      const canvas = await html2canvas(plot.current, {
        useCORS: true,
        allowTaint: true,
        logging: true,
        backgroundColor: "white",
        willReadFrequently: true,
        scale: 4,
      });
      const imgData = canvas.toDataURL("image/png");
      plotsImg.push(imgData);
    }

    return plotsImg;
  };

  const getFileName = () => {
    const protocolTitle = t(getProtocolTitle("protocols", protocolId));
    const protocolTitleParsed = protocolTitle.replace(" ", "_");
    return `${t("patient")}${patientId}_${protocolTitleParsed}.pdf`;
  };

  const generatePDF = async () => {
    const plotsImg = await captureScorePlot();
    const doc = <SessionsPDF plotsImg={plotsImg} />;
    const asPdf = pdf([]);
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();
    saveAs(blob, getFileName());
  };

  return (
    <DownloadButton onClick={generatePDF} disabled={!sessionsData.length} cursor={sessionsData.length ? "pointer" : "auto"}>
      <FileImg src={sessionsData.length ? PDFImg : PDFDisabledImg} alt="ico-pdf" />
    </DownloadButton>
  );
};
export default DownloadPDF;
